import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FUEL_TYPES, FUEL_TYPE_LABELS, type FuelType } from '@shared/domain';
import { ROLE_LABELS, type Role } from '@shared/roles';
import { api } from '../../api';
import { driverInviteText, staffInviteText, whatsappUrl } from '@shared/invites';
import { useSession } from '../../session';
import type { Assignment, Person, Vehicle } from '../../types';
import { Badge, ConfirmDialog, Dialog, Empty, ErrorBox, Field, Loading, RupeeInput, inr, rupeesToPaise, useBusy } from '../../ui';

export function People() {
  const { can, active } = useSession();
  const qc = useQueryClient();
  const [sent, setSent] = useState<{ name: string; phone: string; text: string } | null>(null);
  const fleet = active?.orgName ?? 'Your company';
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api.get<Vehicle[]>('/api/vehicles') });
  const assignments = useQuery({ queryKey: ['assignments'], queryFn: () => api.get<Assignment[]>('/api/assignments') });
  const [dlg, setDlg] = useState<null | 'person' | 'vehicle' | 'assign' | 'staff' | { remove: Person } | { removeV: Vehicle } | { end: Assignment }>(null);
  const [f, setF] = useState<Record<string, string>>({});
  const { busy, error, run, setError } = useBusy();
  const manage = can('buyer.people.manage');
  const close = () => { setDlg(null); setF({}); setError(null); };
  const done = async () => { await qc.invalidateQueries(); close(); };
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const vName = (id: string) => vehicles.data?.find((v) => v.id === id)?.registrationNumber ?? '?';
  const pName = (id: string) => people.data?.find((p) => p.id === id)?.name ?? '?';

  if (people.isLoading) return <Loading />;
  return (
    <div className="stack">
      <div className="card">
        <div className="row between">
          <h1>Drivers</h1>
          {manage ? <button className="btn primary" onClick={() => setDlg('person')}>+ Add driver</button> : null}
        </div>
        <ErrorBox error={people.error} />
        {people.data && !people.data.length ? <Empty>No drivers yet.</Empty> : null}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Mobile</th><th className="num">Used today</th><th className="num">Daily limit</th><th /></tr></thead>
            <tbody>
              {people.data?.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}{p.employeeCode ? <div><small>{p.employeeCode}</small></div> : null}</td>
                  <td>{p.phoneMasked} {p.phoneVerified ? <Badge tone="ok">verified</Badge> : null}</td>
                  <td className="num">{inr(p.usedTodayPaise)}</td>
                  <td className="num">{p.dailyCapPaise ? inr(p.dailyCapPaise) : 'company rule'}</td>
                  <td>{manage ? <button className="btn small" onClick={() => setDlg({ remove: p })}>Remove</button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="row between">
          <h2>Vehicles</h2>
          {can('buyer.vehicles.manage') ? <button className="btn" onClick={() => setDlg('vehicle')}>+ Add vehicle</button> : null}
        </div>
        {vehicles.data && !vehicles.data.length ? <Empty>No vehicles yet.</Empty> : null}
        <ul className="list">
          {vehicles.data?.map((v) => (
            <li key={v.id} className="row between">
              <div><strong>{v.registrationNumber}</strong> · {FUEL_TYPE_LABELS[v.fuelType as FuelType]}{v.lastOdometer ? <small className="muted"> · last odometer {v.lastOdometer} km</small> : null}</div>
              {can('buyer.vehicles.manage') ? <button className="btn small" onClick={() => setDlg({ removeV: v })}>Remove</button> : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <div className="row between">
          <h2>Who drives which vehicle</h2>
          {can('buyer.vehicles.manage') ? <button className="btn" onClick={() => setDlg('assign')}>+ Assign</button> : null}
        </div>
        <p className="muted">A driver can only get fuel for a vehicle assigned to them. Removing an assignment stops their unused indents for that vehicle.</p>
        <ul className="list">
          {assignments.data?.map((a) => (
            <li key={a._id} className="row between">
              <span>{pName(a.personId)} → <strong>{vName(a.vehicleId)}</strong></span>
              {can('buyer.vehicles.manage') ? <button className="btn small" onClick={() => setDlg({ end: a })}>End</button> : null}
            </li>
          ))}
        </ul>
      </div>

      {can('org.members.manage') ? <StaffCard onAdd={() => setDlg('staff')} /> : null}

      <Dialog open={dlg === 'person'} title="Add a driver" onClose={close}>
        <div className="stack">
          <Field label="Name"><input value={f.name ?? ''} onChange={set('name')} /></Field>
          <Field label="Mobile number" hint="Their WhatsApp number: indent codes and QR links go here."><input inputMode="tel" value={f.phone ?? ''} onChange={set('phone')} /></Field>
          <Field label="Employee code (optional)"><input value={f.code ?? ''} onChange={set('code')} /></Field>
          <Field label="Daily limit (₹, optional)" hint="Leave empty to use the company rule."><RupeeInput value={f.daily ?? ''} onChange={(v) => setF({ ...f, daily: v })} /></Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy} onClick={() => void run(async () => { await api.post('/api/people', { name: f.name, phone: f.phone, employeeCode: f.code || undefined, dailyCapPaise: rupeesToPaise(f.daily ?? '') ?? undefined }); setSent({ name: f.name ?? '', phone: f.phone ?? '', text: driverInviteText({ personName: f.name ?? '', businessName: fleet }) }); await done(); })}>Save</button>
        </div>
      </Dialog>
      <Dialog open={dlg === 'vehicle'} title="Add a vehicle" onClose={close}>
        <div className="stack">
          <Field label="Vehicle number"><input value={f.reg ?? ''} onChange={set('reg')} placeholder="MH12AB1234" /></Field>
          <Field label="Fuel">
            <select value={f.fuel ?? ''} onChange={set('fuel')}>
              <option value="">Choose…</option>
              {FUEL_TYPES.map((t) => <option key={t} value={t}>{FUEL_TYPE_LABELS[t]}</option>)}
            </select>
          </Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy || !f.fuel} onClick={() => void run(async () => { await api.post('/api/vehicles', { registrationNumber: f.reg, fuelType: f.fuel }); await done(); })}>Save</button>
        </div>
      </Dialog>
      <Dialog open={dlg === 'assign'} title="Assign a vehicle to a driver" onClose={close}>
        <div className="stack">
          <Field label="Driver"><select value={f.p ?? ''} onChange={set('p')}><option value="">Choose…</option>{people.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Vehicle"><select value={f.v ?? ''} onChange={set('v')}><option value="">Choose…</option>{vehicles.data?.map((v) => <option key={v.id} value={v.id}>{v.registrationNumber}</option>)}</select></Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy || !f.p || !f.v} onClick={() => void run(async () => { await api.post('/api/assignments', { personId: f.p, vehicleId: f.v }); await done(); })}>Assign</button>
        </div>
      </Dialog>
      {dlg && typeof dlg === 'object' && 'remove' in dlg ? (
        <ConfirmDialog open title="Remove this driver?" lines={[['Driver', dlg.remove.name]]} confirmLabel="Remove" danger busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { await api.post(`/api/people/${dlg.remove.id}/remove`, { reason: f.reason || 'Removed' }); await done(); })}>
          <p className="muted">Their unused indents stop working. Past supplies keep their name.</p>
          <Field label="Reason"><input value={f.reason ?? ''} onChange={set('reason')} /></Field>
        </ConfirmDialog>
      ) : null}
      {dlg && typeof dlg === 'object' && 'removeV' in dlg ? (
        <ConfirmDialog open title="Remove this vehicle?" lines={[['Vehicle', dlg.removeV.registrationNumber]]} confirmLabel="Remove" danger busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { await api.post(`/api/vehicles/${dlg.removeV.id}/remove`, { reason: f.reason || 'Removed' }); await done(); })}>
          <Field label="Reason"><input value={f.reason ?? ''} onChange={set('reason')} /></Field>
        </ConfirmDialog>
      ) : null}
      {dlg && typeof dlg === 'object' && 'end' in dlg ? (
        <ConfirmDialog open title="End this assignment?" lines={[['Driver', pName(dlg.end.personId)], ['Vehicle', vName(dlg.end.vehicleId)]]} confirmLabel="End assignment" danger busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => { await api.post(`/api/assignments/${dlg.end._id}/end`, { reason: f.reason || 'Changed' }); await done(); })}>
          <Field label="Reason"><input value={f.reason ?? ''} onChange={set('reason')} /></Field>
        </ConfirmDialog>
      ) : null}
      <Dialog open={dlg === 'staff'} title="Add a company user" onClose={close}>
        <div className="stack">
          <Field label="Name"><input value={f.name ?? ''} onChange={set('name')} /></Field>
          <Field label="Mobile number"><input inputMode="tel" value={f.phone ?? ''} onChange={set('phone')} /></Field>
          <Field label="Role">
            <select value={f.role ?? ''} onChange={set('role')}>
              <option value="">Choose…</option>
              {(['BUYER_APPROVER', 'BUYER_ACCOUNTS', 'BUYER_ADMIN'] as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </Field>
          <ErrorBox error={error} />
          <button className="btn primary" disabled={busy || !f.role} onClick={() => void run(async () => { await api.post('/api/org/members', { name: f.name, phone: f.phone, roles: [f.role] }); setSent({ name: f.name ?? '', phone: f.phone ?? '', text: staffInviteText({ personName: f.name ?? '', businessName: fleet, roleLabel: ROLE_LABELS[f.role as Role], attendant: false, pinSet: false, webBase: window.location.origin }) }); await done(); })}>Add</button>
        </div>
      </Dialog>
      <Dialog open={Boolean(sent)} title={`${sent?.name ?? ''} is added`} onClose={() => setSent(null)}>
        <div className="stack">
          <p className="muted" style={{ whiteSpace: 'pre-line' }}>{sent?.text}</p>
          {sent ? <a className="btn primary" href={whatsappUrl(sent.phone, sent.text)} target="_blank" rel="noreferrer">Send to {sent.name.split(' ')[0]} on WhatsApp</a> : null}
          <button className="btn" onClick={() => setSent(null)}>Done</button>
        </div>
      </Dialog>
    </div>
  );
}

interface Member {
  membershipId: string;
  userId: string;
  name?: string;
  phoneMasked: string;
  roles: Role[];
  status: string;
}

export function StaffCard({ onAdd }: { onAdd: () => void }) {
  const q = useQuery({ queryKey: ['members'], queryFn: () => api.get<Member[]>('/api/org/members') });
  return (
    <div className="card">
      <div className="row between">
        <h2>Company users</h2>
        <button className="btn" onClick={onAdd}>+ Add user</button>
      </div>
      <ul className="list">
        {q.data?.filter((m) => !m.roles.every((r) => r === 'DRIVER')).map((m) => (
          <li key={m.membershipId} className="row between">
            <span><strong>{m.name}</strong> · {m.roles.map((r) => ROLE_LABELS[r]).join(', ')}</span>
            <Badge tone={m.status === 'ACTIVE' ? 'ok' : 'bad'}>{m.status.toLowerCase()}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
